import { Button, FormField, Input, Module, ModuleBody, ModuleHead, PageFrame, Select } from "@vjal/ui";
import { DataTableCollection } from "@vjal/ui";
import type { DataTableColumn } from "@vjal/ui";
import {
  adminCoachWorkflowFixture,
  previewReschedule
} from "../domain/admin-coach-workflows";
import { isLiveCoachingDataMode } from "../hooks/useLiveProductData";
import { LiveSessionRescheduleForm } from "./product-live-workflow-forms";
import { liveRecordIdFromPath } from "./product-live-rows";
import { MembershipExtensionNote } from "./product-z6-membership-extension-note";
import type { ProductScreenContext, ProductScreenContract, TableRow } from "./product-route-contracts";

const activeSession = adminCoachWorkflowFixture.sessions[0];
const reschedulePreview = previewReschedule(activeSession);

export function rescheduleScreen(): ProductScreenContract {
  const rows = isLiveCoachingDataMode()
    ? []
    : [
        {
          id: "before",
          checkpoint: "Before",
          sessionId: reschedulePreview.sessionIdBefore,
          timing: "2026-03-08 14:00 America/New_York",
          history: "current-state"
        },
        {
          id: "after",
          checkpoint: "After",
          sessionId: reschedulePreview.sessionIdAfter,
          timing: "2026-03-09 15:00 America/New_York",
          history: "same session id"
        },
        {
          id: "past",
          checkpoint: "Past sessions",
          sessionId: "unchanged",
          timing: reschedulePreview.pastSessionsUnchanged ? "unchanged" : "requires review",
          history: "event-history-recorded"
        }
      ];

  return {
    title: () => "Reschedule session",
    primaryJob: "Preview before and after timing while preserving the same session identity and connected artifacts.",
    secondaryJob: "Past sessions remain unchanged and Zoom/provider continuity is labeled honestly.",
    workTitle: "Reschedule preview",
    rows,
    columns: rescheduleColumns(),
    states: ["current-state", "preview", "zoom-continuity", "confirmed", "event-history-recorded", "success", "stale-data-prevention"],
    apiFamilies: ["session-service", "history-service"],
    roleDeltas: "Operational writers can reschedule in scope; company admin and participant remain status-only.",
    extensionSlots: ["series-wide future changes", "Zoom refresh", "calendar provider update"],
    mobilePosture: "Before/after rows remain readable below one-off/future-series mode selection.",
    evidenceRequirement: "Playwright must prove same session id, before/after preview, and unchanged past-session copy.",
    surfaceMode: "focused",
    customBody: RescheduleSurface
  };
}

function RescheduleSurface({ role, routePath }: ProductScreenContext) {
  const live = isLiveCoachingDataMode();
  const liveSessionId = live ? liveRecordIdFromPath(routePath, "sessions") : null;
  const rows: TableRow[] = live ? [] : [
    {
      id: "before",
      checkpoint: "Before",
      sessionId: reschedulePreview.sessionIdBefore,
      timing: "2026-03-08 14:00 America/New_York",
      history: "current-state"
    },
    {
      id: "after",
      checkpoint: "After",
      sessionId: reschedulePreview.sessionIdAfter,
      timing: "2026-03-09 15:00 America/New_York",
      history: "same session id"
    },
    {
      id: "past",
      checkpoint: "Past sessions",
      sessionId: "unchanged",
      timing: reschedulePreview.pastSessionsUnchanged ? "unchanged" : "requires review",
      history: "event-history-recorded"
    }
  ];

  return (
    <PageFrame title="Reschedule session" subtitle="Preview impact before confirming a one-off or future-series move." className="col-span-12">
      <section aria-label="Reschedule session" className="flex flex-col gap-4" data-testid="session-reschedule-page">
      {live ? (
        <LiveSessionRescheduleForm role={role} sessionId={liveSessionId} />
      ) : (
      <Module span={12}>
        <ModuleHead title="Before and after" subtitle={reschedulePreview.zoomContinuityLabel} />
        <ModuleBody className="grid gap-4 lg:grid-cols-3">
          <Select label="Reschedule mode" value="one-off" disabled>
            <option value="one-off">One-off session</option>
            <option value="future-series">Future series</option>
          </Select>
          <FormField label="New date and time" hint="Same session id is retained.">
            {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} value="2026-03-09 15:00 America/New_York" readOnly />}
          </FormField>
          <Button type="button">Confirm reschedule preview</Button>
        </ModuleBody>
      </Module>
      )}
      {!live ? (
        <Module span={12}>
          <ModuleHead title="Reschedule impact" subtitle="Past sessions stay unchanged and the selected session id is retained." />
          <ModuleBody flush>
            <DataTableCollection caption="Reschedule impact" columns={rescheduleColumns()} data={rows} stableChipNames />
          </ModuleBody>
        </Module>
      ) : null}
      {/* F3: membership expiry auto-extends when the cohort end-date moves later
          (live-only, consistent with the rest of the reschedule lane). */}
      {live ? <MembershipExtensionNote /> : null}
      </section>
    </PageFrame>
  );
}

function rescheduleColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "checkpoint", header: "Checkpoint" },
    { key: "sessionId", header: "Session id" },
    { key: "timing", header: "Timing" },
    { key: "history", header: "History" }
  ];
}
